import { useState, useRef, useEffect } from "react";
import PrizePicker from "../PrizePicker.jsx";
import PlushImg from "../PlushImg.jsx";
import { randomSize, weightedSize, getType, getColor, SIZES, plushFilter } from "../prizes.js";
import { t, sampleNames } from "../i18n.js";
import { getName } from "../profile.js";
import { ensureCtx, out, speak } from "../audio.js";

/* ============== HESTEVÆDDELØB – carnival roll-a-ball horse race ===============
   Aim & drag back (pinball-style) to roll the ball up the ramp. The ball must
   fly over the edge to land on the hole board: red = 3, blue = 2, yellow = 1.
   Up to 2 balls in play at once — they collide like real balls.
   Points move your horse. First to GOAL wins and picks a plush prize.
================================================================================ */

const W = 360, H = 540;          // logical canvas size
const BALL_R = 10;
const GOAL = 30;                 // lengths to finish
const GATE_S = 1.95;             // nedtælling + ringeklokke før der må skydes (= klokkens 0-slag)
const START_Y = H - 76;          // default ball row inside the placement zone
const ZONE_TOP = H - 110;        // upper dashed line of the placement zone
const ZONE_BOT = START_Y + BALL_R + 4;         // lower dashed line
const PLACE_MIN_Y = ZONE_TOP + BALL_R + 2;     // ball centre must stay between the lines
const PLACE_MAX_Y = ZONE_BOT - BALL_R - 2;
const TRAY = { x: W - 38, y: H - 30, r: 30 };  // ball dish in the corner
const GRAB_R = 26;               // near-ball drags decide by direction: down = shoot, sideways/up = move

const EDGE_Y = 352;              // ramp edge: fly over it to reach the board
const EDGE_MIN = 170;            // min speed to clear the edge
const AIR_TIME = 0.32;           // flight time over the edge
const MAX_BALLS = 2;

/* ---- bane-layouts pr. sværhedsgrad: huller + blokker-pinde (PEGS) ----
   Pinde rammer kun RULLENDE kugler på pladen — luftfasen flyver henover,
   så et hårdt højrisiko-skud OVER vogterne er stadig muligt. */
const RED = "#D93A2B", BLUE = "#3D5FA0", YELLOW = "#E3B923";
const CLASSIC_HOLES = [
  { x: 180, y: 88,  r: 17, pts: 3, c: RED },
  { x: 116, y: 146, r: 16, pts: 2, c: BLUE },
  { x: 244, y: 146, r: 16, pts: 2, c: BLUE },
  { x: 74,  y: 200, r: 16, pts: 2, c: BLUE },
  { x: 286, y: 200, r: 16, pts: 2, c: BLUE },
  { x: 44,  y: 264, r: 15, pts: 1, c: YELLOW },
  { x: 124, y: 240, r: 15, pts: 1, c: YELLOW },
  { x: 236, y: 240, r: 15, pts: 1, c: YELLOW },
  { x: 316, y: 264, r: 15, pts: 1, c: YELLOW },
  { x: 180, y: 296, r: 15, pts: 1, c: YELLOW },
  { x: 88,  y: 322, r: 15, pts: 1, c: YELLOW },
  { x: 272, y: 322, r: 15, pts: 1, c: YELLOW },
];
const LAYOUTS = {
  // Den Klassiske: åben plade, frit sigte mod rød i midten
  let: { holes: CLASSIC_HOLES, pegs: [] },
  // Dobbelt-Guld: TO røde 3'ere bagerst i hjørnerne, hver med en vogterpind
  mellem: {
    holes: [
      { x: 58,  y: 92,  r: 17, pts: 3, c: RED },
      { x: 302, y: 92,  r: 17, pts: 3, c: RED },
      { x: 180, y: 112, r: 16, pts: 2, c: BLUE },
      { x: 104, y: 176, r: 16, pts: 2, c: BLUE },
      { x: 256, y: 176, r: 16, pts: 2, c: BLUE },
      { x: 46,  y: 252, r: 15, pts: 1, c: YELLOW },
      { x: 180, y: 234, r: 15, pts: 1, c: YELLOW },
      { x: 314, y: 252, r: 15, pts: 1, c: YELLOW },
      { x: 112, y: 300, r: 15, pts: 1, c: YELLOW },
      { x: 248, y: 300, r: 15, pts: 1, c: YELLOW },
      { x: 180, y: 322, r: 15, pts: 1, c: YELLOW },
    ],
    pegs: [{ x: 74, y: 148, r: 7 }, { x: 286, y: 148, r: 7 }],
  },
  // Vogteren: klassisk bane, men tre pinde spærrer det direkte midterskud
  svaer: {
    holes: CLASSIC_HOLES,
    pegs: [{ x: 180, y: 148, r: 8 }, { x: 144, y: 192, r: 7 }, { x: 216, y: 192, r: 7 }],
  },
};

// CPU-hestenes navne trækkes tilfældigt fra sprogets pulje (i18n names_horse)
const CPU_COLORS = ["#C8352E", "#E08A1E", "#2E8B57", "#7B4FA0", "#4A4A4A"];
const YOU_COLOR = "#1B5FBF";

/* Speakeren er ALTID engelsk (sprog-invariant, Daniels valg 18/7) og omtaler
   hestene som farve + banenummer — TTS'en radbrækker CPU-navnene. Farveordene
   følger banefarverne YOU_COLOR/CPU_COLORS (spilleren er nr. 1, blå). */
const SPEAK_COLORS = ["blue", "red", "orange", "green", "purple", "black"];
const ANN_OFF = "And they're off!";
const ANN_LEAD = [
  "And now it's number {num}, the {color} horse, in front!",
  "Number {num} takes the lead! Go {color} horse!",
  "Here comes number {num}! The {color} horse hits the front!",
  "It's number {num} now, the {color} horse, out in front!",
];
/* farvekommentarer mellem føringsskiftene (periodisk tjek af stillingen) */
const ANN_AWAY = [
  "{Color} has broken away from the field!",
  "Number {num} is pulling away! Can anyone catch the {color} horse?",
  "The {color} horse is out on its own! What a gap!",
];
const ANN_CATCH = [
  "{Color} is now catching up!",
  "Number {num} is closing in on the leader!",
  "Here comes the {color} horse, closing fast!",
];
const ANN_BACK = [
  "{Color} is struggling at the back of the race!",
  "Number {num} is falling behind the field!",
  "The {color} horse is running out of steam back there!",
];
const ANN_WIN = [
  "And the winner is number {num}, the {color} horse!",
  "Number {num} takes it! The {color} horse wins the race!",
  "It's the {color} horse! Number {num} wins! What a race!",
];
const annFmt = (line, p) => {
  const c = SPEAK_COLORS[p.id] || "grey";
  return line.replace("{num}", String(p.id + 1)).replace("{color}", c)
    .replace("{Color}", c[0].toUpperCase() + c.slice(1));
};

const DIFFS = {
  let:    { label: "Let",    interval: [2.9, 4.0], dist: [0.48, 0.34, 0.13, 0.05], prizes: ["lille"] },
  mellem: { label: "Mellem", interval: [2.5, 3.4], dist: [0.32, 0.35, 0.21, 0.12], prizes: ["lille", "mellem"] },
  svaer:  { label: "Svær",   interval: [2.1, 2.9], dist: [0.22, 0.33, 0.26, 0.19], prizes: ["lille", "mellem", "stor"] },
};

/* ---- tiny synth sounds ---- */
function makeAudio() {
  let ctx = null;
  let noiseBuf = null, galTimer = null, galNext = 0;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g); g.connect(out()); o.start(t); o.stop(t + dur);
  };
  return {
    unlock: ensure,
    launch() { const c = ensure(); if (c) tone(180, c.currentTime, 0.12, "triangle", 0.1); },
    knock() { const c = ensure(); if (c) tone(300, c.currentTime, 0.06, "square", 0.08); },
    plink(pts) {
      const c = ensure(); if (!c) return;
      const base = pts === 3 ? 660 : pts === 2 ? 520 : 420;
      tone(base, c.currentTime, 0.18, "sine", 0.16);
      tone(base * 1.5, c.currentTime + 0.09, 0.22, "sine", 0.1);
    },
    win() {
      const c = ensure(); if (!c) return;
      [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.14, 0.3, "triangle", 0.14));
    },
    countdownBell() {
      // hele nedtællingen + klokken planlagt på AUDIO-uret i ét kald:
      // setTimeout-drift gav hørbar forsinkelse på klokken — nu rammer den
      // PRÆCIS på 0-slaget (3-2-1-KLOKKE, 600 ms pr. slag)
      const c = ensure(); if (!c) return;
      const t0 = c.currentTime + 0.15;
      tone(520, t0, 0.15, "square", 0.1);
      tone(520, t0 + 0.6, 0.15, "square", 0.1);
      tone(880, t0 + 1.2, 0.3, "square", 0.1);
      for (let i = 0; i < 3; i++)
        [1567, 2093, 2637].forEach((f, j) =>
          tone(f * (1 + 0.004 * j), t0 + 1.8 + i * 0.16, 0.45 - i * 0.06, "triangle", 0.07));
    },
    gallopStart() {
      // galop-baggrund: bandpass-filtreret støj som hovslag, to "heste" forskudt
      const c = ensure(); if (!c) return;
      this.gallopStop();
      if (!noiseBuf) {
        noiseBuf = c.createBuffer(1, Math.floor(c.sampleRate * 0.1), c.sampleRate);
        const d = noiseBuf.getChannelData(0);
        for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      }
      const hoof = (t, vol, freq) => {
        const s = c.createBufferSource(); s.buffer = noiseBuf; s.loop = true;
        const f = c.createBiquadFilter(); f.type = "bandpass"; f.frequency.value = freq; f.Q.value = 2.2;
        const gn = c.createGain();
        gn.gain.setValueAtTime(vol, t);
        gn.gain.exponentialRampToValueAtTime(0.001, t + 0.055);
        s.connect(f); f.connect(gn); gn.connect(out());
        s.start(t); s.stop(t + 0.08);
      };
      const STRIDE = 0.44, PAT = [0, 0.075, 0.165, 0.245];   // 4-slags galopmønster
      galNext = c.currentTime + 0.15;
      galTimer = setInterval(() => {
        if (!c || c.state !== "running") return;
        while (galNext < c.currentTime + 0.7) {
          for (const layer of [0, 1]) {
            const off = layer * 0.12, base = layer ? 210 : 150;
            for (const p of PAT) {
              const jit = (Math.random() - 0.5) * 0.02;
              hoof(galNext + p + off + jit, p === 0.245 ? 0.05 : 0.032, base + Math.random() * 50);
            }
          }
          galNext += STRIDE;
        }
      }, 250);
    },
    gallopStop() {
      if (galTimer) { clearInterval(galTimer); galTimer = null; }
    },
  };
}

export default function HorseRace({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | finished
  const [numCpu, setNumCpu] = useState(3);
  const [diffKey, setDiffKey] = useState("mellem");
  const [players, setPlayers] = useState([]);
  const [winner, setWinner] = useState(null);
  const [lastPts, setLastPts] = useState(null);
  const [prizeSize, setPrizeSize] = useState(null);   // random size when you win
  const [claimed, setClaimed] = useState(null);       // chosen prize
  const [showTrack, setShowTrack] = useState(true);   // race track visible / collapsed

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const gameRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const startGame = () => {
    const ps = [{ id: 0, name: getName() || t("you"), color: YOU_COLOR, score: 0, isYou: true }];
    const cpuNames = sampleNames("horse", numCpu);
    for (let i = 0; i < numCpu; i++)
      ps.push({ id: i + 1, name: cpuNames[i], color: CPU_COLORS[i], score: 0, isYou: false });
    setPlayers(ps);
    setWinner(null);
    setLastPts(null);
    setPrizeSize(null);
    setClaimed(null);
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const diff = DIFFS[diffKey];
    const layout = LAYOUTS[diffKey];

    const g = {
      balls: [],                   // {x,y,vx,vy, phase:'placed'|'ramp'|'air'|'board'|'sinking', air, sink}
      carry: null,                 // {x, y} ball being carried from the tray
      pending: null,               // {ball, sx, sy} touch near a placed ball, direction not decided yet
      aim: null,                   // {ball, sx, sy, dx, dy} while dragging
      popups: [],                  // {x, y, txt, c, t}
      cpus: [],
      over: false,
      now: 0,
      leadId: null, leadAt: 0, lastVariant: -1,   // speaker-kommentator
      standing: null, spokeAt: 0, lastComment: "",   // farvekommentarer (stukket af/haler ind/bagerst)
    };
    for (let i = 0; i < numCpu; i++)
      g.cpus.push({ idx: i + 1, next: GATE_S + 1.5 + Math.random() * (diff.interval[1] - diff.interval[0]) + diff.interval[0] });
    gameRef.current = g;

    /* ---- løbsstart: nedtællings-bip, ringeklokke, "Og så er de afsted!" +
       galop-baggrund (kører til løbet er afgjort) ---- */
    const a = audioRef.current;
    const cdPop = (txt) => g.popups.push({ x: W / 2, y: H * 0.42, txt, c: "#e8c15a", t: 0 });
    a.countdownBell();   // bip-bip-bip-KLOKKE på audio-uret (klokken præcis på 0)
    const annTimers = [
      setTimeout(() => cdPop("3"), 150),
      setTimeout(() => cdPop("2"), 750),
      setTimeout(() => cdPop("1"), 1350),
      setTimeout(() => {
        speak(ANN_OFF);
        a.gallopStart();
      }, 2000),
    ];

    /* ---- farvekommentarer: periodisk kig på stillingen, når speakeren har ro ---- */
    const commentTick = () => {
      if (g.over || g.now < GATE_S + 4 || !g.standing) return;
      if (Date.now() - g.spokeAt < 6500) return;        // giv føringsskift/seneste kommentar ro
      const sorted = [...g.standing].sort((x, y) => y.score - x.score);
      const leader = sorted[0], second = sorted[1], last = sorted[sorted.length - 1];
      const cands = [];
      if (second && leader.score - second.score >= 5 && leader.score < GOAL - 3)
        cands.push(["away", leader, ANN_AWAY]);
      if (second && second.score > 0 && leader.score - second.score <= 2 && leader.score >= 8)
        cands.push(["catch", second, ANN_CATCH]);
      if (sorted.length >= 3 && leader.score - last.score >= 8 && last.score < GOAL * 0.7)
        cands.push(["back", last, ANN_BACK]);
      const fresh = cands.filter(([type, p]) => type + p.id !== g.lastComment);
      if (!fresh.length) return;
      const [type, p, pool] = fresh[Math.floor(Math.random() * fresh.length)];
      g.lastComment = type + p.id;
      g.spokeAt = Date.now();
      speak(annFmt(pool[Math.floor(Math.random() * pool.length)], p));
    };
    const annInt = setInterval(commentTick, 4000);

    /* ---- canvas sizing: fit width AND remaining viewport height (mobile) ---- */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
      g.viewScale = scale;
    };
    fit();
    window.addEventListener("resize", fit);
    // refit when layout above the canvas changes (e.g. the race track collapses)
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- input ---- */
    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: (t.clientX - r.left) / (r.width / W), y: (t.clientY - r.top) / (r.height / H) };
    };
    const trayCount = () => MAX_BALLS - g.balls.length - (g.carry ? 1 : 0);
    const down = (e) => {
      audioRef.current.unlock();
      if (g.over) return;
      e.preventDefault();
      const p = pos(e);
      // near a placed ball: wait for the drag direction — down shoots, sideways/up moves
      const pb = g.balls.find((b) => b.phase === "placed" && Math.hypot(p.x - b.x, p.y - b.y) < GRAB_R);
      if (pb) {
        g.pending = { ball: pb, sx: p.x, sy: p.y };
        return;
      }
      // take a new ball from the tray
      if (trayCount() > 0 && Math.hypot(p.x - TRAY.x, p.y - TRAY.y) < TRAY.r + 8) {
        g.carry = { x: p.x, y: p.y };
        return;
      }
      // drag anywhere else: aim the nearest placed ball
      const placed = g.balls.filter((b) => b.phase === "placed");
      if (placed.length) {
        const target = placed.reduce((a, b) =>
          Math.hypot(p.x - a.x, p.y - a.y) <= Math.hypot(p.x - b.x, p.y - b.y) ? a : b);
        g.aim = { ball: target, sx: p.x, sy: p.y, dx: 0, dy: 0 };
      }
    };
    const clampCarry = (p) => ({
      x: Math.max(BALL_R + 8, Math.min(W - BALL_R - 8, p.x)),
      y: Math.max(PLACE_MIN_Y, Math.min(PLACE_MAX_Y, p.y)),   // stays in the zone: drop = what you see
    });
    const move = (e) => {
      if (!g.aim && !g.carry && !g.pending) return;
      e.preventDefault();
      const p = pos(e);
      if (g.pending) {
        const dx = p.x - g.pending.sx, dy = p.y - g.pending.sy;
        if (Math.hypot(dx, dy) > 12) {
          const ball = g.pending.ball;
          if (dy > Math.abs(dx)) {
            g.aim = { ball, sx: g.pending.sx, sy: g.pending.sy, dx, dy };
          } else {
            g.balls = g.balls.filter((b) => b !== ball);
            g.carry = clampCarry(p);
          }
          g.pending = null;
        }
        return;
      }
      if (g.aim) {
        g.aim.dx = p.x - g.aim.sx;
        g.aim.dy = p.y - g.aim.sy;
      } else {
        const c = clampCarry(p);
        g.carry.x = c.x; g.carry.y = c.y;
      }
    };
    const up = (e) => {
      if (!g.aim && !g.carry && !g.pending) return;
      e.preventDefault();
      if (g.pending) { g.pending = null; return; }   // a plain tap: ball stays put
      // drop the carried ball inside the placement zone, where it was released
      if (g.carry) {
        let x = Math.max(BALL_R + 8, Math.min(W - BALL_R - 8, g.carry.x));
        const y = Math.max(PLACE_MIN_Y, Math.min(PLACE_MAX_Y, g.carry.y));
        g.carry = null;
        // don't stack on top of another placed ball
        for (const b of g.balls)
          if (b.phase === "placed" && Math.hypot(b.x - x, b.y - y) < BALL_R * 2)
            x = b.x + (x >= b.x ? BALL_R * 2 + 1 : -(BALL_R * 2 + 1));
        g.balls.push({ x, y, vx: 0, vy: 0, phase: "placed", air: null, sink: null });
        return;
      }
      // release the aim: launch the placed ball
      const { ball, dx, dy } = g.aim;
      g.aim = null;
      if (g.now < GATE_S) return;                        // vent på klokken — kuglen bliver stående
      const len = Math.hypot(dx, dy);
      if (len < 14 || dy < 6) return;                    // must drag downwards; ball stays placed
      const power = Math.min(len, 190) / 190;            // 0..1
      const ang = Math.atan2(-dy, -dx);                  // opposite of drag
      const speed = 260 + power * 620;                   // px/s
      ball.vx = Math.cos(ang) * speed;
      ball.vy = Math.sin(ang) * speed;
      ball.phase = "ramp";
      audioRef.current.launch();
    };
    canvas.addEventListener("mousedown", down);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    canvas.addEventListener("touchstart", down, { passive: false });
    canvas.addEventListener("touchmove", move, { passive: false });
    canvas.addEventListener("touchend", up, { passive: false });

    /* ---- scoring ---- */
    const addScore = (idx, pts) => {
      if (g.over || pts <= 0) return;
      setPlayers((prev) => {
        const next = prev.map((p, i) => (i === idx ? { ...p, score: Math.min(GOAL, p.score + pts) } : p));
        const w = next.find((p) => p.score >= GOAL);
        if (w && !g.over) {
          g.over = true;
          setWinner(w);
          // on Hard, the BIG-plush odds grow with the number of opponents
          if (w.isYou) setPrizeSize(
            diffKey === "svaer"
              ? weightedSize({ lille: 40, mellem: 35, stor: 15 + numCpu * 9 })
              : randomSize(diff.prizes)
          );
          audioRef.current.gallopStop();
          audioRef.current.win();
          // speakeren når at annoncere vinderen, FØR præmie/stilling dækker banen
          speak(annFmt(ANN_WIN[Math.floor(Math.random() * ANN_WIN.length)], w), { interrupt: true });
          setTimeout(() => setPhase("finished"), 2400);
        } else if (!w) {
          g.standing = next;   // frisk stilling til farvekommentarerne
          // speaker: ny hest i front (varierede replikker, cooldown mod pladder)
          const top = Math.max(...next.map((p) => p.score));
          if (top > 0) {
            const cands = next.filter((p) => p.score === top);
            if (!cands.some((p) => p.id === g.leadId) && Date.now() - g.leadAt > 3500) {
              const lead = cands[0];
              let v;
              do { v = Math.floor(Math.random() * ANN_LEAD.length); } while (v === g.lastVariant);
              g.lastVariant = v;
              g.leadId = lead.id;
              g.leadAt = Date.now();
              g.spokeAt = Date.now();
              speak(annFmt(ANN_LEAD[v], lead), { interrupt: true });
            }
          }
        }
        return next;
      });
    };
    g.addScore = addScore;
    if (localStorage.getItem("carnival.debug")) window.__hr = g;   // debug-krog (bot: __hr.addScore(idx, pts))

    /* ---- physics ---- */
    const stepBall = (b, dt) => {
      if (b.phase === "placed") return;    // waiting on the start line
      if (b.phase === "sinking") {
        b.sink.t += dt;
        if (b.sink.t > 0.55) b.dead = true;
        return;
      }

      if (b.phase === "air") {
        // flying over the edge: no friction, no slope
        b.air.t += dt;
        b.x += b.vx * dt; b.y += b.vy * dt;
        if (b.x < BALL_R + 6) { b.x = BALL_R + 6; b.vx = Math.abs(b.vx) * 0.65; }
        if (b.x > W - BALL_R - 6) { b.x = W - BALL_R - 6; b.vx = -Math.abs(b.vx) * 0.65; }
        if (b.y < BALL_R + 8) { b.y = BALL_R + 8; b.vy = Math.abs(b.vy) * 0.5; }
        if (b.air.t >= AIR_TIME) { b.phase = "board"; b.air = null; }
        return;
      }

      const onBoard = b.phase === "board";
      b.vy += (onBoard ? 130 : 150) * dt;                    // slope pulls back down
      const damp = Math.exp(-(onBoard ? 1.15 : 0.55) * dt);  // board felt brakes harder
      b.vx *= damp; b.vy *= damp;
      b.x += b.vx * dt; b.y += b.vy * dt;

      // walls
      if (b.x < BALL_R + 6) { b.x = BALL_R + 6; b.vx = Math.abs(b.vx) * 0.65; }
      if (b.x > W - BALL_R - 6) { b.x = W - BALL_R - 6; b.vx = -Math.abs(b.vx) * 0.65; }
      if (b.y < BALL_R + 8) { b.y = BALL_R + 8; b.vy = Math.abs(b.vy) * 0.5; }

      const spd = Math.hypot(b.vx, b.vy);

      // ramp edge: fast enough → take off; too slow → roll back
      if (!onBoard && b.y <= EDGE_Y) {
        if (b.vy < 0 && spd >= EDGE_MIN) {
          b.phase = "air";
          b.air = { t: 0 };
        } else {
          b.y = EDGE_Y;
          b.vy = Math.abs(b.vy) * 0.4;
        }
        return;
      }
      // the edge is a wall from the board side: balls can't roll back down the ramp.
      // They rest against the lip and after 0.5s the carny drops them back in the tray.
      if (onBoard && b.y >= EDGE_Y - BALL_R + 2) {
        b.y = EDGE_Y - BALL_R + 2;
        if (b.vy > 0) b.vy = -b.vy * 0.2;
        b.edgeT = (b.edgeT || 0) + dt;
        if (b.edgeT > 0.5) { b.dead = true; return; }
      }

      // blocker pegs: solid posts on the board — rolling balls bounce off
      if (onBoard) {
        for (const p of layout.pegs) {
          const dx = b.x - p.x, dy = b.y - p.y;
          const d = Math.hypot(dx, dy);
          if (d > 0 && d < BALL_R + p.r) {
            const nx = dx / d, ny = dy / d;
            b.x = p.x + nx * (BALL_R + p.r);
            b.y = p.y + ny * (BALL_R + p.r);
            const vn = b.vx * nx + b.vy * ny;
            if (vn < 0) {
              b.vx -= 2 * vn * nx;
              b.vy -= 2 * vn * ny;
              b.vx *= 0.72; b.vy *= 0.72;
              audioRef.current.knock();
            }
          }
        }
      }

      // holes only exist on the board
      if (onBoard) {
        for (const h of layout.holes) {
          const d = Math.hypot(b.x - h.x, b.y - h.y);
          if (d < h.r - 2 && spd < 190) {
            b.phase = "sinking";
            b.sink = { hole: h, t: 0 };
            g.popups.push({ x: h.x, y: h.y - 20, txt: "+" + h.pts, c: h.c, t: 0 });
            audioRef.current.plink(h.pts);
            addScore(0, h.pts);
            setLastPts(h.pts);
            break;
          } else if (d < h.r + 2 && spd >= 190) {
            // rattles over the hole
            b.vx += (b.x - h.x) * 2.2; b.vy += (b.y - h.y) * 2.2;
            b.vx *= 0.94; b.vy *= 0.94;
          }
        }
      }

      // returned to the bottom: ball is spent
      if (b.phase === "ramp" && b.y > H - 30 && b.vy > 0) b.dead = true;
    };

    const step = (dt) => {
      g.now += dt;

      // CPU shots
      for (const cpu of g.cpus) {
        if (g.over) break;
        if (g.now >= cpu.next) {
          cpu.next = g.now + diff.interval[0] + Math.random() * (diff.interval[1] - diff.interval[0]);
          const r = Math.random(); let acc = 0, pts = 0;
          for (let i = 0; i < 4; i++) { acc += diff.dist[i]; if (r < acc) { pts = i; break; } }
          if (pts > 0) addScore(cpu.idx, pts);
        }
      }

      for (const b of g.balls) stepBall(b, dt);

      // ball vs ball: equal-mass elastic collision (skip airborne/sinking balls)
      for (let i = 0; i < g.balls.length; i++) {
        for (let j = i + 1; j < g.balls.length; j++) {
          const a = g.balls[i], b = g.balls[j];
          if (a.phase === "air" || a.phase === "sinking") continue;
          if (b.phase === "air" || b.phase === "sinking") continue;
          const dx = b.x - a.x, dy = b.y - a.y;
          const d = Math.hypot(dx, dy);
          if (d <= 0 || d >= BALL_R * 2) continue;
          const nx = dx / d, ny = dy / d;
          const overlap = (BALL_R * 2 - d) / 2;
          a.x -= nx * overlap; a.y -= ny * overlap;
          b.x += nx * overlap; b.y += ny * overlap;
          const p = (a.vx - b.vx) * nx + (a.vy - b.vy) * ny;
          if (p > 0) {
            // swap velocity components along the collision normal
            a.vx -= p * nx; a.vy -= p * ny;
            b.vx += p * nx; b.vy += p * ny;
            // a knocked waiting ball starts rolling
            if (a.phase === "placed") a.phase = "ramp";
            if (b.phase === "placed") b.phase = "ramp";
            audioRef.current.knock();
          }
        }
      }

      g.balls = g.balls.filter((b) => !b.dead);

      g.popups.forEach((p) => (p.t += dt));
      g.popups = g.popups.filter((p) => p.t < 1);
    };

    /* ---- drawing ---- */
    const drawBallShape = (x, y, r) => {
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = "#e04a2f"; ctx.fill();
      ctx.beginPath(); ctx.arc(x - r * 0.3, y - r * 0.3, r * 0.35, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(255,255,255,0.8)"; ctx.fill();
    };

    const draw = () => {
      // grønt læder-look (Gemini-mockuppet): dyb olivengrøn m. narv og mottling
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, "#69823f");
      grad.addColorStop(1, "#556b33");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      for (let i = 0; i < 7; i++) {            // læder-mottling (deterministisk)
        const mx = (i * 151) % W, my2 = (i * 233) % H;
        ctx.fillStyle = i % 2 ? "rgba(255,255,230,0.04)" : "rgba(20,30,5,0.06)";
        ctx.beginPath(); ctx.ellipse(mx, my2, 80, 52, i * 1.3, 0, Math.PI * 2); ctx.fill();
      }
      for (let i = 0; i < 170; i++) {          // narv
        ctx.fillStyle = i % 2 ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.05)";
        ctx.fillRect((i * 89) % W, (i * 197) % H, 1.6, 1.6);
      }

      // hole board sits lower than the ramp: shadow cast just beyond the edge
      const eg = ctx.createLinearGradient(0, EDGE_Y - 26, 0, EDGE_Y);
      eg.addColorStop(0, "rgba(0,0,0,0)");
      eg.addColorStop(1, "rgba(0,0,0,0.28)");
      ctx.fillStyle = eg;
      ctx.fillRect(6, EDGE_Y - 26, W - 12, 26);
      // kanten markeres af en messinglinje (mockuppets guldstribe)
      ctx.fillStyle = "#c8a557"; ctx.fillRect(6, EDGE_Y, W - 12, 2.5);
      ctx.fillStyle = "rgba(50,32,8,0.45)"; ctx.fillRect(6, EDGE_Y + 2.5, W - 12, 1.5);

      // mahogni-sider + toprail
      const railG = ctx.createLinearGradient(0, 0, 10, 0);
      railG.addColorStop(0, "#7a4526"); railG.addColorStop(1, "#4f2812");
      ctx.fillStyle = railG;
      ctx.fillRect(0, 0, 7, H);
      const railG2 = ctx.createLinearGradient(W - 10, 0, W, 0);
      railG2.addColorStop(0, "#4f2812"); railG2.addColorStop(1, "#7a4526");
      ctx.fillStyle = railG2;
      ctx.fillRect(W - 7, 0, 7, H);
      ctx.fillStyle = "#5d3119";
      ctx.fillRect(0, 0, W, 9);
      ctx.fillStyle = "rgba(255,220,160,0.22)";
      ctx.fillRect(0, 8, W, 1.5);

      // huller som metal-grommets: ring i farvens metal + glas-inderring
      const RINGS = {
        [RED]:    { hi: "#c07a52", lo: "#6a2c1a", glas: "#6e1512" },   // kobber + rødt glas
        [BLUE]:   { hi: "#6f9bd9", lo: "#24406e", glas: "#182c4e" },   // blåt eloxeret metal
        [YELLOW]: { hi: "#ecc76a", lo: "#96702c", glas: "#6e4f14" },   // messing/rav
      };
      for (const h of layout.holes) {
        const rg2 = RINGS[h.c] || { hi: h.c, lo: h.c, glas: "#111" };
        ctx.fillStyle = "rgba(0,0,0,0.22)";            // blød skygge
        ctx.beginPath(); ctx.ellipse(h.x + 2, h.y + 3, h.r + 5, h.r + 3, 0, 0, Math.PI * 2); ctx.fill();
        const mg = ctx.createRadialGradient(h.x - h.r * 0.45, h.y - h.r * 0.5, 2, h.x, h.y, h.r + 5);
        mg.addColorStop(0, rg2.hi);
        mg.addColorStop(0.7, rg2.lo);
        mg.addColorStop(1, rg2.hi);
        ctx.fillStyle = mg;
        ctx.beginPath(); ctx.arc(h.x, h.y, h.r + 5, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = rg2.glas;                      // glas-inderring
        ctx.beginPath(); ctx.arc(h.x, h.y, h.r, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#100c06";                     // selve hullet
        ctx.beginPath(); ctx.arc(h.x, h.y, h.r - 4, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.35)";    // glans-bue på ringen
        ctx.lineWidth = 1.6;
        ctx.beginPath(); ctx.arc(h.x, h.y, h.r + 3, -2.4, -1.1); ctx.stroke();
        ctx.lineWidth = 1;
      }

      // blocker pegs: messing-skruehoveder m. blød skygge og kærv
      for (const p of layout.pegs) {
        ctx.beginPath(); ctx.ellipse(p.x + 2, p.y + 3, p.r + 2, p.r * 0.7, 0, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.28)"; ctx.fill();
        const pg2 = ctx.createRadialGradient(p.x - p.r * 0.35, p.y - p.r * 0.4, 1, p.x, p.y, p.r);
        pg2.addColorStop(0, "#f0d59a"); pg2.addColorStop(0.7, "#b98a3a"); pg2.addColorStop(1, "#8a621f");
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = pg2; ctx.fill();
        ctx.strokeStyle = "#7a5a22"; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.strokeStyle = "rgba(90,60,15,0.7)";        // kærv
        ctx.beginPath(); ctx.moveTo(p.x - p.r * 0.5, p.y); ctx.lineTo(p.x + p.r * 0.5, p.y); ctx.stroke();
        ctx.lineWidth = 1;
      }

      // placement zone between the two dashed lines; glows while carrying a ball
      if (g.carry) {
        ctx.fillStyle = "rgba(255,240,190,0.08)";
        ctx.fillRect(8, ZONE_TOP, W - 16, ZONE_BOT - ZONE_TOP);
      }
      // syede guldlinjer (mockuppets stikninger)
      ctx.strokeStyle = "rgba(230,200,140,0.55)";
      ctx.setLineDash([6, 6]);
      ctx.beginPath(); ctx.moveTo(10, ZONE_TOP); ctx.lineTo(W - 10, ZONE_TOP); ctx.stroke();
      ctx.setLineDash([]);

      // lower line of the placement zone
      ctx.strokeStyle = "rgba(230,200,140,0.35)";
      ctx.setLineDash([3, 8]);
      ctx.beginPath(); ctx.moveTo(12, ZONE_BOT); ctx.lineTo(W - 12, ZONE_BOT); ctx.stroke();
      ctx.setLineDash([]);

      // kugle-bakken: læder-lomme m. stålskål (mockuppets hjørne)
      ctx.beginPath(); ctx.arc(TRAY.x, TRAY.y, TRAY.r + 3, 0, Math.PI * 2);
      ctx.fillStyle = "#5d3119"; ctx.fill();
      const sg2 = ctx.createRadialGradient(TRAY.x - 8, TRAY.y - 9, 3, TRAY.x, TRAY.y, TRAY.r);
      sg2.addColorStop(0, "#9aa2ae"); sg2.addColorStop(0.75, "#4c535d"); sg2.addColorStop(1, "#6d7580");
      ctx.beginPath(); ctx.arc(TRAY.x, TRAY.y, TRAY.r, 0, Math.PI * 2);
      ctx.fillStyle = sg2; ctx.fill();
      ctx.beginPath(); ctx.arc(TRAY.x, TRAY.y, TRAY.r - 5, 0, Math.PI * 2);
      ctx.fillStyle = "#1d2126"; ctx.fill();
      const inTray = MAX_BALLS - g.balls.length - (g.carry ? 1 : 0);
      for (let i = 0; i < inTray; i++)
        drawBallShape(TRAY.x - 11 + i * 22, TRAY.y, BALL_R);
      if (inTray === 0 && !g.carry && g.balls.length === 0) {
        ctx.font = "600 10px system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        ctx.fillText(t("tray_empty"), TRAY.x, TRAY.y + 3);
      }

      // aim guide from the placed ball being aimed
      if (g.aim) {
        const { ball, dx, dy } = g.aim;
        const len = Math.min(Math.hypot(dx, dy), 190);
        if (len > 10 && dy > 0) {
          const ang = Math.atan2(-dy, -dx);
          ctx.strokeStyle = "rgba(255,255,255,0.85)";
          ctx.lineWidth = 3; ctx.setLineDash([8, 7]);
          ctx.beginPath();
          ctx.moveTo(ball.x, ball.y);
          ctx.lineTo(ball.x + Math.cos(ang) * (60 + len * 1.3), ball.y + Math.sin(ang) * (60 + len * 1.3));
          ctx.stroke(); ctx.setLineDash([]); ctx.lineWidth = 1;
          // power meter
          ctx.fillStyle = "rgba(0,0,0,0.35)";
          ctx.fillRect(W / 2 - 60, H - 24, 120, 10);
          ctx.fillStyle = len / 190 > 0.75 ? "#D93A2B" : "#E3B923";
          ctx.fillRect(W / 2 - 60, H - 24, 120 * (len / 190), 10);
        }
      }

      // ball being carried from the tray
      if (g.carry) {
        ctx.beginPath(); ctx.arc(g.carry.x, g.carry.y + 6, BALL_R, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.2)"; ctx.fill();
        drawBallShape(g.carry.x, g.carry.y - 4, BALL_R * 1.15);
      }

      // balls in play
      for (const b of g.balls) {
        if (b.phase === "sinking") {
          const k = 1 - Math.min(b.sink.t / 0.4, 1);
          if (k > 0) {
            ctx.beginPath(); ctx.arc(b.sink.hole.x, b.sink.hole.y, BALL_R * k, 0, Math.PI * 2);
            ctx.fillStyle = "#e04a2f"; ctx.fill();
          }
          continue;
        }
        const lift = b.phase === "air"
          ? Math.sin(Math.PI * Math.min(b.air.t / AIR_TIME, 1)) * 18
          : 0;
        // fading out while the carny picks it up at the edge
        if (b.edgeT) ctx.globalAlpha = Math.max(0, 1 - Math.max(0, b.edgeT - 0.25) / 0.25);
        ctx.beginPath();
        ctx.arc(b.x, b.y + 3 + lift * 0.5, BALL_R * (1 - lift / 90), 0, Math.PI * 2);
        ctx.fillStyle = "rgba(0,0,0,0.25)"; ctx.fill();
        drawBallShape(b.x, b.y - lift, BALL_R * (1 + lift / 60));
        ctx.globalAlpha = 1;
      }

      // popups
      for (const p of g.popups) {
        ctx.globalAlpha = 1 - p.t;
        ctx.font = "bold 26px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 5; ctx.strokeStyle = "#fff";
        ctx.strokeText(p.txt, p.x, p.y - p.t * 34);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, p.x, p.y - p.t * 34);
        ctx.globalAlpha = 1;
      }

      // hint (gylden serif som mockuppets bundtekst)
      if (!g.aim && !g.carry) {
        const anyPlaced = g.balls.some((b) => b.phase === "placed");
        const txt = g.balls.length === 0
          ? t("h_hint_take")
          : anyPlaced ? t("h_hint_move") : null;
        if (txt) {
          ctx.font = "600 12.5px Georgia, serif";
          ctx.textAlign = "center";
          ctx.fillStyle = "rgba(235,205,145,0.95)";
          // maxWidth klemmer lange oversættelser, så teksten aldrig klippes
          ctx.fillText(txt, (W - 76) / 2 + 10, H - 12, W - 96);
        }
      }

      // vignette + tynd guld-pinstripe langs mahognisiderne
      const vg = ctx.createRadialGradient(W / 2, H / 2, 200, W / 2, H / 2, 420);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(0,0,0,0.22)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, W, H);
      ctx.strokeStyle = "rgba(255,220,160,0.28)"; ctx.lineWidth = 1.5;
      ctx.strokeRect(8.5, 10.5, W - 17, H - 12);
      ctx.lineWidth = 1;
    };

    let raf, last = performance.now();
    const loop = (t) => {
      const dt = Math.min((t - last) / 1000, 0.033);
      last = t;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      annTimers.forEach(clearTimeout);
      clearInterval(annInt);
      a.gallopStop();
      try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) { /* valgfri */ }
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("mousedown", down);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      canvas.removeEventListener("touchstart", down);
      canvas.removeEventListener("touchmove", move);
      canvas.removeEventListener("touchend", up);
    };
  }, [phase, numCpu, diffKey]);

  /* ================= UI ================= */
  const sorted = [...players].sort((a, b) => b.score - a.score);
  const youWon = winner && winner.isYou;
  const yourRank = sorted.findIndex((p) => p.isYou) + 1;

  return (
    <div className={"gal-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      {/* Booth header */}
      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <div className="bunting">
          {Array.from({ length: 14 }).map((_, i) => <span key={i} className={i % 2 ? "fl fl-w" : "fl"} />)}
        </div>
        <h1>{t("tile_horse").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("h_sign2")}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("h_lede", { goal: GOAL })}</p>
          <div className="legend">
            <span><i className="dot" style={{ background: "#D93A2B" }} /> {t("h_legend_red")}</span>
            <span><i className="dot" style={{ background: "#3D5FA0" }} /> {t("h_legend_blue")}</span>
            <span><i className="dot" style={{ background: "#E3B923" }} /> {t("h_legend_yellow")}</span>
          </div>

          <h2>{t("h_opponents")}</h2>
          <div className="opts">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} className={"opt" + (numCpu === n ? " sel" : "")} onClick={() => setNumCpu(n)}>{n}</button>
            ))}
          </div>

          <h2>{t("h_difficulty")}</h2>
          <div className="opts">
            {Object.keys(DIFFS).map((k) => (
              <button key={k} className={"opt wide" + (diffKey === k ? " sel" : "")} onClick={() => setDiffKey(k)}>{t("diff_" + k)}</button>
            ))}
          </div>
          <p className="trackline">{t("h_track_" + diffKey)}</p>
          <p className="prizenote">{t("h_prizenote")}</p>

          <button className="cta" onClick={startGame}>{t("h_start")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          {/* Race track (collapsible so the whole lane fits on phone screens) */}
          {showTrack && (
          <section className="track" aria-label="Hestevæddeløb">
            {players.map((p, i) => (
              <div key={p.id} className={"lane" + (i % 2 ? " alt" : "")}>
                <span className={"lanename" + (p.isYou ? " you" : "")}>
                  {p.isYou ? "★ " : ""}{p.name}
                </span>
                <div className="finish" />
                <div
                  className="horse"
                  style={{
                    // start efter navnepladen (92px) og land med NÆSEN på målstregen
                    // ved GOAL: 94% - 10px = stregens venstre kant, 43px = hestens bredde
                    left: `calc(92px + ${(p.score / GOAL).toFixed(4)} * (94% - 145px))`,
                    transition: "left .5s cubic-bezier(.34,1.4,.5,1)",
                  }}
                >
                  <span className="saddle" style={{ background: p.color }}>{p.id + 1}</span>
                  <img className="emoji" src="/images/thumbs/horse.png" alt="" />
                </div>
              </div>
            ))}
          </section>
          )}

          <div className="scorebar">
            <span>{t("h_yourhorse")} <b>{players[0] ? players[0].score : 0} / {GOAL}</b></span>
            <button className="trackbtn" onClick={() => setShowTrack((v) => !v)}>
              <img className="btnhorse" src="/images/thumbs/horse.png" alt="" /> {yourRank || "-"} {showTrack ? "▲" : "▼"}
            </button>
            {lastPts !== null && <span className="last">+{lastPts}</span>}
          </div>

          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "finished" && winner && youWon && !claimed && prizeSize && (
        <PrizePicker size={prizeSize} game="Hestevæddeløb" onDone={(p) => setClaimed(p)} />
      )}

      {phase === "finished" && winner && (!youWon || claimed) && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{youWon ? t("h_win") : t("h_lose")}</div>
            <p className="winline"><b style={{ color: winner.color }}>{winner.name}</b> {t("h_wonrace")}</p>
            {claimed && (
              <p className="prizeline">
                <PlushImg className="prizeemoji" type={claimed.type} color={claimed.color} px={SIZES[claimed.size].pick} />
                <br />
                {t("your_prize")} <b>{t("color_" + claimed.color).toLowerCase()} {t("type_" + claimed.type).toLowerCase()}</b> ({t("size_" + claimed.size).toLowerCase()}) {t("in_room")}
              </p>
            )}
            {!youWon && <p className="noprize">{t("h_noprize")}</p>}
            <ol className="results">
              {sorted.map((p) => (
                <li key={p.id}>
                  <span className="saddle sm" style={{ background: p.color }}>{p.id + 1}</span>
                  {p.name} — {p.score} {t("h_points")}
                </li>
              ))}
            </ol>
            <button className="cta" onClick={() => setPhase("setup")}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.gal-root{
  --red:#C8352E; --cream:#F6EED9; --ink:#22301c; --felt:#6a9a42;
  min-height:100vh; background:#24401f;
  background-image:radial-gradient(circle at 50% -10%, #3d6b2f 0%, #24401f 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.bunting{display:flex; justify-content:center; gap:2px; margin-bottom:4px;}
.fl{width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:14px solid var(--red);}
.fl-w{border-top-color:var(--cream);}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(22px,6.4vw,38px);
  margin:0; letter-spacing:1px; color:var(--cream);
  text-shadow:0 3px 0 #7e1f1a, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
/* compact header while playing: every pixel goes to the lane on phones */
.ingame .bunting{display:none;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(20px,5vw,28px); text-shadow:0 2px 0 #7e1f1a, 0 4px 8px rgba(0,0,0,.45);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.legend{display:flex; gap:14px; font-weight:800; font-size:14px; margin-bottom:6px; flex-wrap:wrap;}
.dot{display:inline-block; width:13px; height:13px; border-radius:50%; margin-right:5px; vertical-align:-2px;}
.panel h2{font-family:'Alfa Slab One',Georgia,serif; font-size:15px; letter-spacing:1px; margin:16px 0 8px; color:#7e1f1a;}
.prizenote{margin:10px 0 0; font-size:12px; font-weight:800; opacity:.75; line-height:1.4;}
.trackline{margin:10px 0 0; font-size:12.5px; font-weight:800; color:#5a3d1e; line-height:1.4;}
.opts{display:flex; gap:8px; flex-wrap:wrap;}
.opt{
  flex:1; min-width:44px; padding:10px 0; font-size:17px; font-weight:800;
  background:#fff; border:2px solid #d8cfae; border-radius:8px; color:var(--ink); cursor:pointer;
}
.opt.wide{min-width:80px;}
.opt.sel{background:var(--red); border-color:#7e1f1a; color:#fff;}
.cta{
  width:100%; margin-top:20px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.alt{margin-top:10px; background:#5c7a4a; box-shadow:0 4px 0 #37492c; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #37492c;}
.track{
  width:min(96%,520px); margin-top:10px; border-radius:10px; overflow:hidden;
  border:4px solid #6b3a1e; box-shadow:0 8px 22px rgba(0,0,0,.4);
}
.lane{
  position:relative; height:30px; background-color:#55794a;
  background-image:
    repeating-linear-gradient(90deg, rgba(255,255,255,.045) 0 4px, rgba(0,0,0,0) 4px 11px),
    linear-gradient(rgba(255,255,255,.07), rgba(0,0,0,.10));
  border-bottom:2px solid rgba(246,238,217,.5);
}
.lane:last-child{border-bottom:none;}
.lane.alt{background-color:#4b6d41;}
.lanename{
  position:absolute; left:0; top:50%; transform:translateY(-50%); z-index:1;
  padding:1.5px 8px 1.5px 6px; font-size:9.5px; font-weight:800; letter-spacing:.3px;
  background:linear-gradient(180deg,#e6cd85,#b8903f);
  color:#3a2810; border:1px solid #7c5d16; border-left:none;
  border-radius:0 6px 6px 0;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.5), 0 2px 4px rgba(0,0,0,.35);
}
.lanename.you{background:linear-gradient(180deg,#ffe9a8,#d9ae4e); color:#2c1e08;}
.finish{
  position:absolute; right:6%; top:0; bottom:0; width:10px;
  background:repeating-conic-gradient(#f2ead2 0 25%, #16181c 0 50%) 0 0/10px 10px;
  box-shadow:0 0 4px rgba(0,0,0,.4);
}
.horse{position:absolute; top:50%; transform:translateY(-50%); display:flex; align-items:center; gap:2px; z-index:2;}
.horse .emoji{height:24px; width:auto; display:inline-block; transform:scaleX(-1); filter:drop-shadow(0 2px 2px rgba(0,0,0,.4));}
.saddle{
  display:inline-flex; align-items:center; justify-content:center;
  width:15px; height:15px; border-radius:4px; color:#fff; font-size:10px; font-weight:800;
  border:1.5px solid rgba(255,255,255,.85); box-shadow:0 1px 3px rgba(0,0,0,.4);
}
.saddle.sm{width:20px; height:20px; margin-right:8px;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.trackbtn{
  background:rgba(255,255,255,.15); color:var(--cream); border:none; border-radius:20px;
  font-family:inherit; font-weight:800; font-size:13px; padding:4px 14px; cursor:pointer;
}
.trackbtn:active{background:rgba(255,255,255,.3);}
.btnhorse{height:16px; width:auto; vertical-align:-3px; transform:scaleX(-1);}
.last{background:rgba(255,255,255,.15); padding:2px 10px; border-radius:20px;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #6b3a1e; box-shadow:0 12px 30px rgba(0,0,0,.45);
}
.boardwrap canvas{display:block; touch-action:none;}
.overlay{
  position:fixed; inset:0; background:rgba(10,20,8,.72); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:24px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.winline{font-size:17px; font-weight:800; margin:4px 0 10px;}
.prizeline{font-size:14px; font-weight:800; margin:0 0 12px; line-height:1.5;}
.prizeemoji{line-height:1.2; display:inline-block;}
.noprize{font-size:13px; font-weight:800; opacity:.7; margin:0 0 12px;}
.results{list-style:none; padding:0; margin:0 0 6px; text-align:left;}
.results li{
  display:flex; align-items:center; font-weight:800; font-size:15px;
  padding:7px 10px; border-bottom:1px dashed #cbbf9c;
}
.results li:first-child{background:#fff6d8; border-radius:8px;}
@media (prefers-reduced-motion: reduce){ .horse{transition:none !important;} }
`;
